import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { appConfirm } from '../../lib/dialogs';
import EconomyReportCard from '../economy/reportCard';
import { summarizeEconomyReports, dataUrlToBase64 } from '../../lib/conventionEconomy';
import { overlapsFor, hasIncludedOverlap } from '../../lib/economyOverlap';

const ACCEPT = '.xlsx,.xls,.csv,.txt,.tsv';
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const kr = n => `${util.format(n || 0, 0)} kr`;


/**
 * Stevneøkonomi drawer — officials only (opened from the kanban header).
 *
 * One picture of what the stevne brought in, from three sources:
 *   1. Startavgift from the kanban (external starts × price, paid/unpaid) —
 *      passed in as props so the drawer shows exactly what the header shows.
 *   2. The app's own Vipps checkout payments (/payment-totals).
 *   3. Payment-provider reports the officials upload (xlsx/csv). The backend
 *      flattens the file and has Claude extract one fixed JSON shape, validated
 *      in backend/lib/convention-report-extract.js; the result lives on
 *      convention.vippsReports. See lib/conventionEconomy.js for how the
 *      reports are summed.
 */
@observer
class ConventionEconomy extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadingPayments: true,
            paymentTotals: null,
            uploading: false,
            uploadFilename: '',
            error: null,
            expanded: {},
        };
        this.fileInput = null;
    }

    componentDidMount() {
        this.loadPayments();
    }

    getReports = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        return Array.isArray(convention?.vippsReports) ? convention.vippsReports : [];
    }

    setReports = reports => {
        const { conventionStore } = this.props.stores;
        conventionStore.updateObjectKeyValue('convention', 'vippsReports', reports);
    }

    loadPayments = async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) {
            this.setState({ loadingPayments: false });
            return;
        }
        const response = await util.fetchApi(`/api/conventions/${convention.id}/payment-totals`, { method: 'GET' });
        if (response && response.status === 200 && response.data) {
            this.setState({ loadingPayments: false, paymentTotals: response.data });
        } else {
            this.setState({ loadingPayments: false, paymentTotals: null });
        }
    }

    pickFile = () => {
        if (this.fileInput) {
            this.fileInput.value = '';
            this.fileInput.click();
        }
    }

    onFileChosen = async e => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (file.size > MAX_FILE_BYTES) {
            this.setState({ error: 'Filen er for stor (maks 5 MB).' });
            return;
        }
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id) return;

        this.setState({ uploading: true, uploadFilename: file.name, error: null });
        let fileBase64;
        try {
            fileBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(dataUrlToBase64(reader.result));
                reader.onerror = () => reject(new Error('Kunne ikke lese filen'));
                reader.readAsDataURL(file);
            });
        } catch (err) {
            this.setState({ uploading: false, error: err.message });
            return;
        }

        const response = await util.fetchApi(`/api/conventions/${convention.id}/vipps-reports`, { method: 'POST' }, {
            filename: file.name,
            fileBase64,
        });
        if (response && response.status === 201 && response.data && Array.isArray(response.data.vippsReports)) {
            this.setReports(response.data.vippsReports);
            const md5 = response.data.report && response.data.report.md5;
            this.setState(prev => ({
                uploading: false,
                uploadFilename: '',
                expanded: md5 ? { ...prev.expanded, [md5]: true } : prev.expanded,
            }));
        } else {
            this.setState({
                uploading: false,
                uploadFilename: '',
                error: response?.message || 'Kunne ikke tolke rapporten',
            });
        }
    }

    deleteReport = report => async () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id || !report?.md5) return;
        if (!(await appConfirm(`Fjerne rapporten «${report.filename}»?`))) return;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/vipps-reports/${report.md5}`, { method: 'DELETE' }, {});
        if (response && (response.status === 202 || response.status === 200)) {
            this.setReports(this.getReports().filter(r => !r || r.md5 !== report.md5));
        } else {
            this.setState({ error: response?.message || 'Kunne ikke fjerne rapporten' });
        }
    }

    // Overlapping reports: flip whether this one counts in the sum (PATCH
    // includeInSum); the backend returns the whole updated array.
    toggleInclude = report => async includeInSum => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention?.id || !report?.md5) return;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/vipps-reports/${report.md5}`, { method: 'PATCH' }, {
            includeInSum,
        });
        if (response && response.status === 202 && response.data && Array.isArray(response.data.vippsReports)) {
            this.setReports(response.data.vippsReports);
        } else {
            this.setState({ error: response?.message || 'Kunne ikke oppdatere rapporten' });
        }
    }

    toggleExpanded = md5 => () => {
        this.setState(prev => ({ expanded: { ...prev.expanded, [md5]: !prev.expanded[md5] } }));
    }

    renderStartFees() {
        const { income, external, organizer } = this.props;
        if (!income) return null;
        const hasPrice = income.feePrice > 0;
        return (
            <div class='card mb-3'>
                <div class='card-body py-2'>
                    <div class='d-flex justify-content-between align-items-center'>
                        <span class='fw-bold'>
                            <i class='fa-duotone fa-person-rifle me-2' />
                            <Text id='convention.economy-start-fees'>Start fees</Text>
                        </span>
                        {hasPrice && <span class='fs-5 fw-bold'>{kr(income.expectedAmount)}</span>}
                    </div>
                    <div class='small text-body-secondary mt-1'>
                        {external && (
                            <div>
                                <Text
                                    id='convention.economy-external-line'
                                    fields={{ shooters: external.uniqueShooters, starts: external.totalStarts }}
                                >
                                    External: {external.uniqueShooters} shooters · {external.totalStarts} starts
                                </Text>
                                {organizer && (
                                    <span class='ms-2'>
                                        <Text
                                            id='convention.economy-organizer-line'
                                            fields={{ shooters: organizer.uniqueShooters, starts: organizer.totalStarts }}
                                        >
                                            Organizer: {organizer.uniqueShooters} shooters · {organizer.totalStarts} starts (free)
                                        </Text>
                                    </span>
                                )}
                            </div>
                        )}
                        {hasPrice ? (
                            <div>
                                <span>{income.starts} × {kr(income.feePrice)}</span>
                                <span class='ms-3 text-success-emphasis'>
                                    <i class='fa-solid fa-check me-1' />
                                    <Text id='convention.economy-paid' fields={{ starts: income.paidStarts, amount: kr(income.paidAmount) }}>
                                        Paid {income.paidStarts} starts · {kr(income.paidAmount)}
                                    </Text>
                                </span>
                                {income.unpaidStarts > 0 && (
                                    <span class='ms-3 text-danger-emphasis'>
                                        <i class='fa-solid fa-hourglass-half me-1' />
                                        <Text id='convention.economy-unpaid' fields={{ starts: income.unpaidStarts, amount: kr(income.unpaidAmount) }}>
                                            Unpaid {income.unpaidStarts} starts · {kr(income.unpaidAmount)}
                                        </Text>
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div>
                                <Text id='convention.economy-no-price'>Set the signup product price on the convention to see the expected start-fee income.</Text>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderAppPayments() {
        const { loadingPayments, paymentTotals } = this.state;
        return (
            <div class='card mb-3'>
                <div class='card-body py-2'>
                    <div class='d-flex justify-content-between align-items-center'>
                        <span class='fw-bold'>
                            <i class='fa-duotone fa-mobile-screen-button me-2' />
                            <Text id='convention.economy-app-payments'>Payments through the app</Text>
                        </span>
                        {loadingPayments ? (
                            <i class='fa-solid fa-spinner fa-spin' />
                        ) : (
                            <span class='fs-5 fw-bold'>{kr(paymentTotals?.conventionPaidTotal)}</span>
                        )}
                    </div>
                    <div class='small text-body-secondary mt-1'>
                        {!loadingPayments && (
                            <span>
                                <Text id='convention.economy-app-payments-count' fields={{ count: paymentTotals?.conventionPaymentsCount || 0 }}>
                                    {paymentTotals?.conventionPaymentsCount || 0} payments
                                </Text>
                                {' · '}
                            </span>
                        )}
                        <Text id='convention.economy-app-payments-note'>
                            Already included in a settlement report that covers the same Vipps number — not added twice.
                        </Text>
                    </div>
                </div>
            </div>
        );
    }

    renderSum(reports) {
        const s = summarizeEconomyReports(reports);
        if (s.reportCount === 0) return null;
        const doubleCounting = hasIncludedOverlap(reports);
        return (
            <div class={`card mb-3 ${doubleCounting ? 'border-danger' : 'border-primary'}`}>
                <div class='card-body py-2'>
                    <div class='fw-bold mb-1'>
                        <i class='fa-duotone fa-calculator me-2' />
                        <Text id='convention.economy-sum'>Sum from the reports</Text>
                    </div>
                    {doubleCounting && (
                        <div class='alert alert-danger py-1 mb-2 small'>
                            <i class='fa-solid fa-layer-group me-1' />
                            <Text id='economy.sum-double-counting'>Two counted reports overlap — the sum counts the same money twice. Switch one of them off.</Text>
                        </div>
                    )}
                    <div class='row text-center'>
                        <div class='col-4'>
                            <div class='small text-body-secondary'><Text id='convention.economy-gross'>Gross</Text></div>
                            <div class='fs-5 fw-bold'>{kr(s.gross)}</div>
                        </div>
                        <div class='col-4'>
                            <div class='small text-body-secondary'><Text id='convention.economy-fees'>Vipps fees</Text></div>
                            <div class='fs-5 fw-bold text-danger-emphasis'>−{kr(s.fees)}</div>
                        </div>
                        <div class='col-4'>
                            <div class='small text-body-secondary'><Text id='convention.economy-net'>Net</Text></div>
                            <div class='fs-4 fw-bold text-success-emphasis'>{kr(s.net)}</div>
                        </div>
                    </div>
                    <div class='small text-body-secondary mt-1'>
                        {s.grossSource === 'sales' && (
                            <Text id='convention.economy-gross-from-sales' fields={{ count: s.salesCount }}>
                                Gross from {s.salesCount} sales report(s); fees from the settlement reports.
                            </Text>
                        )}
                        {s.grossSource === 'settlement' && (
                            <Text id='convention.economy-gross-from-settlement' fields={{ count: s.settlementCount }}>
                                Gross and fees from {s.settlementCount} settlement report(s).
                            </Text>
                        )}
                        {s.missing.includes('settlement') && (
                            <span class='ms-1'>
                                <Text id='convention.economy-missing-settlement'>No settlement report yet — fees are unknown.</Text>
                            </span>
                        )}
                        {s.missing.includes('sales') && s.grossSource === 'settlement' && (
                            <span class='ms-1'>
                                <Text id='convention.economy-missing-sales'>No sales report yet — no per-product breakdown.</Text>
                            </span>
                        )}
                        {s.otherCount > 0 && (
                            <span class='ms-1'>
                                <Text id='convention.economy-other-not-summed' fields={{ count: s.otherCount }}>
                                    {s.otherCount} report(s) of unknown kind are not included.
                                </Text>
                            </span>
                        )}
                        {s.payouts > 0 && (
                            <span class='ms-1'>
                                <Text id='convention.economy-payouts' fields={{ amount: kr(s.payouts) }}>Payouts scheduled: {kr(s.payouts)}.</Text>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { uploading, uploadFilename, error } = this.state;
        if (!convention || !convention.id) {
            return null;
        }
        const reports = this.getReports();

        return (
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100 px-3' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            <i class='fa-duotone fa-coins me-2' />
                            <Text id='convention.economy-title'>Convention economy</Text>
                        </span>
                        <small class='text-body-secondary text-truncate ms-2'>{convention.title}</small>
                    </div>

                    <div class='mt-3'>
                        {this.renderStartFees()}
                        {this.renderAppPayments()}
                        {this.renderSum(reports)}

                        <div class='d-flex justify-content-between align-items-center mb-2'>
                            <span class='fw-bold'>
                                <i class='fa-duotone fa-file-invoice me-2' />
                                <Text id='convention.economy-reports'>Reports from the payment provider</Text>
                                {reports.length > 0 && <span class='badge text-bg-secondary ms-2'>{reports.length}</span>}
                            </span>
                            <span>
                                <input
                                    type='file'
                                    accept={ACCEPT}
                                    style='display: none;'
                                    ref={c => { this.fileInput = c; }}
                                    onChange={this.onFileChosen}
                                />
                                <button
                                    type='button'
                                    class='btn btn-sm btn-primary rounded-pill'
                                    onClick={this.pickFile}
                                    disabled={uploading}
                                >
                                    {uploading ? (<>
                                        <i class='fa-solid fa-spinner fa-spin me-1' />
                                        <Text id='convention.economy-ai-reading'>AI is reading the report…</Text>
                                    </>) : (<>
                                        <i class='fa-solid fa-upload me-1' />
                                        <Text id='convention.economy-upload'>Upload report</Text>
                                    </>)}
                                </button>
                            </span>
                        </div>
                        {uploading && uploadFilename && (
                            <div class='small text-body-secondary mb-2'>
                                <i class='fa-regular fa-file me-1' />{uploadFilename}
                            </div>
                        )}
                        {error && (
                            <div class='alert alert-danger py-2 small'>
                                {error}
                                <button type='button' class='btn-close btn-sm float-end' onClick={() => this.setState({ error: null })} />
                            </div>
                        )}
                        <div class='small text-body-secondary mb-2'>
                            <Text id='convention.economy-upload-help'>
                                Upload the sales report and the settlement report from Vipps (xlsx or csv). The AI reads any layout; check the figures it flags.
                            </Text>
                        </div>
                        {reports.length === 0 && !uploading && (
                            <div class='text-center text-body-secondary py-4'>
                                <i class='fa-duotone fa-file-circle-question fa-2x mb-2' />
                                <div><Text id='convention.economy-no-reports'>No reports uploaded yet.</Text></div>
                            </div>
                        )}
                        {reports.map(r => (
                            <EconomyReportCard
                                key={r.md5}
                                report={r}
                                expanded={!!this.state.expanded[r.md5]}
                                onToggle={this.toggleExpanded(r.md5)}
                                onDelete={this.deleteReport(r)}
                                overlaps={overlapsFor(r, reports)}
                                onToggleInclude={this.toggleInclude(r)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}

export default ConventionEconomy;
